'use client';

import { cn } from '@/lib/utils';

import { BlockHeader, BlockHeaderContent } from '@/cms/BlockHeaderContentDC';
import { BlockWrapper } from '@/cms/BlockWrapperDC';
import { BlockComponentModel } from '@/cms/types/blok-component';
import Badge from '@/components/atoms/badge';
import { IconButton } from '@/components/atoms/IconButton';
import ImageStrapi, {
  ImageStrapiProps,
} from '@/components/atoms/Image/ImageStrapi';
import LinkButtonStrapi, {
  LinkButtonStrapiProps,
} from '@/components/atoms/LinkButton/LinkStrapi';
import RichTextFieldStrapi, {
  RichTextFieldStrapiProps,
} from '@/components/atoms/RichTextField/RichTextFieldStrapi';
import { Text } from '@/components/atoms/Text';
import {
  ToggleCMSProps,
  useCMSToggle,
} from '@/components/atoms/Toggle/useCMSToggle';
import { SECTION_NAMES } from '@/integrations/trackingScripts/constants';

import CardsWithHeader from '.';

import iconDownloadSrc from '~/icons/download-02.svg';
import { SvgIcon } from '@/components/SvgIcon';

export interface CardsWithHeaderStrapiProps {
  header?: BlockHeader;
  cards: {
    icon: ImageStrapiProps;
    title: string;
    description: RichTextFieldStrapiProps;
    button: LinkButtonStrapiProps;
    downloadLink?: string;
    badgeText?: string;
  }[];
  toggle?: ToggleCMSProps;
}

export default function CardsWithHeaderStrapi({
  block,
  ...rest
}: BlockComponentModel<CardsWithHeaderStrapiProps>) {
  const { cards, header, toggle } = block;
  const { filteredToggleList, toggleNode: ToggleNode } = useCMSToggle<
    typeof cards
  >(cards, toggle);

  function transformCard(card: (typeof cards)[number]) {
    return {
      icon: (
        <ImageStrapi
          inner={{ width: '32', height: '32' }}
          block={{ ...card.icon }}
        />
      ),
      title: (
        <Text
          color='base-black'
          variant='subtitle-l-d'
          classNames={{ wrapper: 'font-serif' }}
        >
          {card.title}
        </Text>
      ),
      description: (
        <RichTextFieldStrapi
          block={card.description}
          classNames={{ paragraph: 'text-body-l' }}
        />
      ),
      footerLeft: (
        <LinkButtonStrapi
          block={card.button}
          inner={{
            slim: true,
            variant: 'text',
            color: 'primary',
            showRightChevron: true,
          }}
        />
      ),
      footerRight: card.downloadLink ? (
        <IconButton
          variant='text'
          href={card.downloadLink}
          as='a'
          target='_blank'
          classNames={{ root: 'text-brand-500 p-0' }}
        >
          <SvgIcon src={iconDownloadSrc} />
        </IconButton>
      ) : undefined,
      badge: card.badgeText ? (
        <Badge
          variant='soft'
          color='gray'
          classNames={{ root: 'absolute top-3 right-3 z-10' }}
        >
          {card.badgeText}
        </Badge>
      ) : undefined,
    };
  }

  return (
    <BlockWrapper
      {...rest}
      data-tracker-section={SECTION_NAMES.CARDS_WITH_HEADER}
    >
      <BlockHeaderContent
        {...header}
        layoutOptions={{ constrainWidthWithPadding: true }}
        topChildren={ToggleNode ? ToggleNode() : undefined}
      >
        <div
          className={cn(
            'flex w-full flex-wrap justify-center gap-6',
            'm:grid-cols-3 m:grid'
          )}
        >
          {cards.map((card) => (
            <div
              key={card.title}
              className={cn(
                'w-full',
                filteredToggleList?.length &&
                  !filteredToggleList?.some(
                    (item) => card.title === item.title
                  ) &&
                  'hide-content'
              )}
            >
              <CardsWithHeader {...transformCard(card)} />
            </div>
          ))}
        </div>
      </BlockHeaderContent>
    </BlockWrapper>
  );
}
